import React from 'react'
import { Box, Card, CardContent, Typography, Stack, alpha } from '@mui/material'
import { motion } from 'framer-motion'
import { usePageHeader } from '../../contexts/PageHeaderContext'
import { crmColors } from '../../theme/colors'

export const PAGE_ACTIONS_BAR_SX = {
  display: 'flex',
  gap: 1.5,
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  alignItems: 'center',
  px: 2.5,
  py: 1.25,
  minHeight: 52,
  flexShrink: 0,
  backgroundColor: 'var(--color-primary-surface-muted)',
  borderBottom: '1px solid var(--border-color-hover)',
  borderRadius: 'var(--radius-md) var(--radius-md) 0 0',
}

/** Title bar — same solid gold as Search School */
export const PAGE_ACTIONS_BAR_DARK_SX = {
  ...PAGE_ACTIONS_BAR_SX,
  background: '#A26C02',
  backgroundImage: 'none',
  borderBottom: '1px solid #8A5C01',
}

/**
 * Page shell: dark content header (back on right) + card with actions bar + body.
 */
const PageContentLayout = ({
  title,
  subtitle,
  barTitle,
  barSubtitle,
  breadcrumbs,
  backTo,
  onBack,
  showBack = true,
  titleIcon,
  actions,
  children,
  contentPadding = 3,
  cardSx = {},
  barSx = {},
  bareContent = false,
  darkBar = true,
}) => {
  usePageHeader({
    title,
    subtitle,
    breadcrumbs,
    backTo,
    onBack,
    showBack,
    titleIcon,
  })

  const displayBarTitle = barTitle ?? title
  const displayBarSubtitle = barSubtitle ?? subtitle

  return (
    <Box>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        <Card
          elevation={0}
          sx={{
            bgcolor: '#fff',
            boxShadow: 'none !important',
            transform: 'none !important',
            '&::before': { display: 'none' },
            '&:hover': {
              boxShadow: 'none !important',
              transform: 'none !important',
              borderColor: crmColors.border.default,
            },
            ...(bareContent ? { display: 'flex', flexDirection: 'column' } : {}),
            ...cardSx,
          }}
        >
          {(displayBarTitle || actions) && (
            <Box
              className="page-actions-bar"
              sx={{
                ...(darkBar ? PAGE_ACTIONS_BAR_DARK_SX : PAGE_ACTIONS_BAR_SX),
                ...barSx,
              }}
            >
              <Box>
                {displayBarTitle &&
                  (typeof displayBarTitle === 'string' ? (
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        lineHeight: 1.2,
                        color: darkBar ? '#fff' : 'inherit',
                      }}
                    >
                      {displayBarTitle}
                    </Typography>
                  ) : (
                    displayBarTitle
                  ))}
                {displayBarSubtitle &&
                  (typeof displayBarSubtitle === 'string' ? (
                    <Typography
                      variant="caption"
                      sx={{
                        lineHeight: 1.2,
                        color: darkBar ? alpha('#fff', 0.78) : 'text.secondary',
                      }}
                    >
                      {displayBarSubtitle}
                    </Typography>
                  ) : (
                    displayBarSubtitle
                  ))}
              </Box>
              {actions && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {actions}
                </Stack>
              )}
            </Box>
          )}
          {bareContent ? (
            children
          ) : (
            <CardContent sx={{ p: contentPadding, bgcolor: '#fff' }}>{children}</CardContent>
          )}
        </Card>
      </motion.div>
    </Box>
  )
}

export default PageContentLayout
